import type { SketchSymbolKind } from '../models/floorplan-sketch';
import {
  boxHeightOf, boxWidthOf, labelRegions, orientedBoxOf, simplifiedRing, topLeftOf, traceBoundary, type Bitmap, type Region,
} from './raster';

/**
 * Telling the symbols a plan draws for its fittings — a toilet, a washbasin, a bath, a shower
 * tray, a hob — from each other and from everything else standing on the floor, by the shape of
 * the drawing alone.
 *
 * Each shape is described by what does not change when it is drawn bigger, smaller or turned:
 * how long it is against how wide, how much of its box it fills, how many closed loops it is
 * drawn with and whether one sits inside another, how round its inner shape is, how much of its
 * outline is straight, and two of Hu's moment invariants. Each symbol has a template of what
 * those are when it is drawn the usual way (`SYMBOL_TEMPLATES`, measured off the reference
 * drawings in `test-drawings`), and a shape is the symbol it is close enough to, with a
 * confidence for how close. A desk, a chair, a column or a table is none of them.
 *
 * Pure over pixels, like the rest of the tracer, and all of it written here.
 */

/** The fittings recognised. Each is furniture on the plan, with its icon. */
export type SymbolKind = SketchSymbolKind;

export const SYMBOL_KINDS: readonly SymbolKind[] = ['toilet', 'sink', 'bathtub', 'shower', 'hob'];

/** The icon each is shown with: all in both the portal's icon set and the app's. */
export const SYMBOL_ICONS: Record<SymbolKind, string> = {
  toilet: 'toilet',
  sink: 'faucet',
  bathtub: 'bathtub',
  shower: 'shower',
  hob: 'stove',
};

export interface ShapeDescriptors {
  /** The long side of its turned box over the short side: 1 or more. */
  readonly aspect: number;
  /** How much of its turned box the shape covers, loops filled in: 1 for a rectangle, 0.79 for an ellipse. */
  readonly solidity: number;
  /** The closed loops of paper inside it. */
  readonly holes: number;
  /** The separate pieces of ink drawn inside it, like the basin in a washbasin. */
  readonly nested: number;
  /** How much of its box the inner shape fills: the largest piece inside it, or else its largest loop. */
  readonly innerFill: number;
  /** How much of the whole the inner shape covers: a bath's tub most of it, a desk's screen a little. */
  readonly innerShare: number;
  /** How much of its outline is long straight edges: 1 for a box, 0 for a circle. */
  readonly straightShare: number;
  /** Hu's first two moment invariants of the shape, loops filled in. */
  readonly hu1: number;
  readonly hu2: number;
  /** Its long side in metres, when the plan's size is known. */
  readonly lengthMetres: number | null;
}

/** The descriptors a template expects a value of. */
type Measure = 'aspect' | 'solidity' | 'innerFill' | 'innerShare' | 'straightShare' | 'hu1';

interface Range {
  readonly min: number;
  readonly max: number;
}

export interface SymbolTemplate {
  readonly kind: SymbolKind;
  /** What must hold for a shape to be this symbol at all. */
  readonly holes: Range;
  readonly nested: Range;
  /** What the symbol measures drawn the usual way, and how far a drawing may stray from it. */
  readonly expect: Readonly<Partial<Record<Measure, { mean: number; spread: number }>>>;
  /**
   * How long it really is, when the plan's size says: generous, because a size read off the
   * doors is only as right as the doors are the usual width.
   */
  readonly metres: Range;
}

/**
 * What each symbol measures, from the reference drawings (`symbols` in `test-drawings`, which
 * `symbol-shapes.test` measures again, so the two cannot drift apart). The spreads are how far a
 * hand-drawn or differently drawn one may stray on each before it counts against it.
 */
export const SYMBOL_TEMPLATES: readonly SymbolTemplate[] = [
  {
    kind: 'toilet',
    holes: { min: 2, max: 2 },
    nested: { min: 0, max: 0 },
    expect: {
      aspect: { mean: 1.36, spread: 0.3 },
      solidity: { mean: 0.77, spread: 0.1 },
      innerFill: { mean: 0.76, spread: 0.1 },
      // How much of a toilet's outline is straight, and how much of it its loops fill, differ
      // with how it is drawn — the cistern's width against the bowl's, and the pen's.
      innerShare: { mean: 0.33, spread: 0.25 },
      straightShare: { mean: 0.21, spread: 0.5 },
    },
    metres: { min: 0.4, max: 1.1 },
  },
  {
    kind: 'sink',
    holes: { min: 2, max: 3 },
    nested: { min: 1, max: 2 },
    expect: {
      aspect: { mean: 1.35, spread: 0.35 },
      solidity: { mean: 1, spread: 0.08 },
      innerFill: { mean: 0.85, spread: 0.1 },
      innerShare: { mean: 0.32, spread: 0.15 },
      straightShare: { mean: 1, spread: 0.2 },
    },
    metres: { min: 0.3, max: 1.4 },
  },
  {
    kind: 'bathtub',
    holes: { min: 2, max: 3 },
    nested: { min: 1, max: 2 },
    expect: {
      aspect: { mean: 2.23, spread: 0.45 },
      solidity: { mean: 1, spread: 0.08 },
      innerFill: { mean: 0.97, spread: 0.05 },
      innerShare: { mean: 0.64, spread: 0.15 },
      straightShare: { mean: 1, spread: 0.2 },
    },
    metres: { min: 1.1, max: 2.4 },
  },
  {
    kind: 'shower',
    holes: { min: 4, max: 4 },
    nested: { min: 0, max: 0 },
    expect: {
      aspect: { mean: 1, spread: 0.25 },
      solidity: { mean: 1, spread: 0.08 },
      innerFill: { mean: 0.51, spread: 0.12 },
      straightShare: { mean: 1, spread: 0.2 },
    },
    metres: { min: 0.6, max: 1.6 },
  },
  {
    kind: 'hob',
    holes: { min: 5, max: 9 },
    nested: { min: 4, max: 4 },
    expect: {
      aspect: { mean: 1, spread: 0.3 },
      solidity: { mean: 1, spread: 0.08 },
      innerFill: { mean: 0.88, spread: 0.1 },
      straightShare: { mean: 1, spread: 0.2 },
    },
    metres: { min: 0.4, max: 1.2 },
  },
];

/** The confidence of a perfect match, and how much each spread off it costs. */
const BEST_CONFIDENCE = 0.9;
const CONFIDENCE_PER_SPREAD = 0.1;
/** What not knowing the plan's size, so not checking the symbol's, costs. */
const UNSIZED_COST = 0.05;

/** The least a symbol's confidence can be, when it is one at all. */
export const LEAST_CONFIDENCE = 0.5;

/** A patch of `bitmap` as a labelled region, for the shape tools that take one. */
const asRegion = (bitmap: Bitmap, include: (index: number) => boolean): { labels: Int32Array; region: Region } | null => {
  const { width, height } = bitmap;
  const labels = new Int32Array(width * height).fill(-1);
  const region: Region = {
    label: 0, area: 0, minX: width, maxX: -1, minY: height, maxY: -1, sumX: 0, sumY: 0, sumXX: 0, sumYY: 0, sumXY: 0, touchesEdge: false,
  };

  for (let y = 0; y < height; y += 1) {
    for (let x = 0; x < width; x += 1) {
      const index = (y * width) + x;
      if (include(index)) {
        labels[index] = 0;
        region.area += 1;
        region.minX = Math.min(region.minX, x);
        region.maxX = Math.max(region.maxX, x);
        region.minY = Math.min(region.minY, y);
        region.maxY = Math.max(region.maxY, y);
        region.sumX += x;
        region.sumY += y;
        region.sumXX += x * x;
        region.sumYY += y * y;
        region.sumXY += x * y;
      }
    }
  }

  return region.area ? { labels, region } : null;
};

/** How much of its turned box a patch fills, and how big it is. */
const fillOf = (bitmap: Bitmap, include: (index: number) => boolean): { fill: number; area: number } => {
  const patch = asRegion(bitmap, include);
  return patch ? { fill: orientedBoxOf(patch.labels, bitmap.width, patch.region).fill, area: patch.region.area } : { fill: 0, area: 0 };
};

/**
 * The smallest turned box round an outline, trying the direction of each of its edges: for a
 * shape drawn with straight sides, one of them is the answer, at whatever angle it is drawn.
 */
const tightBoxOf = (ring: readonly [number, number][]): { length: number; breadth: number } => {
  let best = { length: 0, breadth: 0, area: Infinity };

  ring.forEach(([x, y], index) => {
    const [nx, ny] = ring[(index + 1) % ring.length];
    const angle = Math.atan2(ny - y, nx - x);
    const [ux, uy] = [Math.cos(angle), Math.sin(angle)];
    let [minU, maxU, minV, maxV] = [Infinity, -Infinity, Infinity, -Infinity];

    ring.forEach(([px, py]) => {
      const u = (px * ux) + (py * uy);
      const v = (py * ux) - (px * uy);
      minU = Math.min(minU, u);
      maxU = Math.max(maxU, u);
      minV = Math.min(minV, v);
      maxV = Math.max(maxV, v);
    });

    const [along, across] = [maxU - minU, maxV - minV];
    if (along * across < best.area) {
      best = { length: Math.max(along, across), breadth: Math.min(along, across), area: along * across };
    }
  });

  return best;
};

/** Hu's first two moment invariants of a patch: the same however big it is drawn, and however turned. */
const huOf = (region: Region): [number, number] => {
  const { area } = region;
  const [mx, my] = [region.sumX / area, region.sumY / area];
  const mu20 = (region.sumXX / area) - (mx * mx);
  const mu02 = (region.sumYY / area) - (my * my);
  const mu11 = (region.sumXY / area) - (mx * my);
  // Normalised for size: the second moments over the area, divided by the area again.
  const [eta20, eta02, eta11] = [mu20 / area, mu02 / area, mu11 / area];
  return [eta20 + eta02, ((eta20 - eta02) ** 2) + (4 * eta11 * eta11)];
};

/**
 * What a drawn shape is like, from its ink: `ink` is 1 for the shape's ink (thickened as the
 * tracer thickens it) and 0 for the paper round and inside it, with at least a pixel of paper on
 * every side. `stroke` is the width a line is drawn, thickened, and `metresPerPixel` the plan's
 * size when it is known.
 */
export const shapeDescriptorsOf = (ink: Bitmap, stroke: number, metresPerPixel: number | null = null): ShapeDescriptors | null => {
  const { width, height, data } = ink;
  const paper = labelRegions(ink, 0, false);
  const inkParts = labelRegions(ink, 1, true);
  const smallest = Math.max(3, stroke * stroke);

  // The shape with its loops filled in: everything but the paper round it.
  const outsideLabels = new Set(paper.regions.filter((region) => region.touchesEdge).map((region) => region.label));
  const silhouette = asRegion(ink, (index) => data[index] === 1 || !outsideLabels.has(paper.labels[index]));

  if (!silhouette || !inkParts.regions.length) {
    return null;
  }

  const holes = paper.regions.filter((region) => !region.touchesEdge && region.area >= smallest);
  // The largest piece of ink is the shape's own outline; what else there is lies inside it.
  const [, ...inside] = [...inkParts.regions].sort((a, b) => b.area - a.area);
  const nested = inside.filter((part) => part.area >= smallest);

  // The inner shape: the largest piece drawn inside, with its own loops filled in; or else the
  // largest loop of the shape itself.
  let inner = { fill: 0, area: 0 };
  if (nested.length) {
    const [largest] = nested;
    const piece = labelRegions(
      { width, height, data: Uint8Array.from(inkParts.labels, (label) => (label === largest.label ? 1 : 0)) },
      0,
      false,
    );
    const pieceOutside = new Set(piece.regions.filter((region) => region.touchesEdge).map((region) => region.label));
    inner = fillOf(ink, (index) => inkParts.labels[index] === largest.label || !pieceOutside.has(piece.labels[index]));
  } else if (holes.length) {
    const largest = holes.reduce((best, hole) => (hole.area > best.area ? hole : best));
    inner = fillOf(ink, (index) => paper.labels[index] === largest.label);
  }

  // How much of the outline runs in long straight edges.
  const inShape = (x: number, y: number): boolean => x >= 0 && y >= 0 && x < width && y < height
    && silhouette.labels[(y * width) + x] === 0;
  const traced = traceBoundary(inShape, topLeftOf(silhouette.labels, width, silhouette.region), silhouette.region.area * 4);
  // Simplified by less than a stroke for a small shape: a small bowl is still a curve.
  const size = Math.max(boxWidthOf(silhouette.region), boxHeightOf(silhouette.region));
  const ring = simplifiedRing(traced, Math.max(1, Math.min(stroke * 0.75, size * 0.03)));
  const edges = ring.map(([x, y], index) => {
    const [nx, ny] = ring[(index + 1) % ring.length];
    return Math.hypot(nx - x, ny - y);
  });
  const perimeter = edges.reduce((sum, edge) => sum + edge, 0);
  const straight = edges.filter((edge) => edge >= perimeter * 0.1).reduce((sum, edge) => sum + edge, 0);

  const [hu1, hu2] = huOf(silhouette.region);

  // The outline runs through the middles of the edge pixels: the shape is a pixel wider each way.
  const tight = tightBoxOf(ring);
  const box = { length: tight.length + 1, breadth: tight.breadth + 1 };

  return {
    aspect: box.length / Math.max(1, box.breadth),
    solidity: Math.min(1, silhouette.region.area / Math.max(1, box.length * box.breadth)),
    holes: holes.length,
    nested: nested.length,
    innerFill: inner.fill,
    innerShare: inner.area / silhouette.region.area,
    straightShare: perimeter > 0 ? straight / perimeter : 0,
    hu1,
    hu2,
    // Thickened a pixel each side.
    lengthMetres: metresPerPixel ? Math.max(1, box.length - 2) * metresPerPixel : null,
  };
};

export interface RecognisedSymbol {
  readonly kind: SymbolKind;
  readonly confidence: number;
}

const within = (value: number, { min, max }: Range): boolean => value >= min && value <= max;

/**
 * The symbol a shape is, and how sure that is: the template it is nearest, if it has the loops
 * that symbol is drawn with and is the size it really is. How near is counted in spreads off
 * what the template expects: a perfect match is 0.9, and every spread off costs 0.1. Null when
 * it is no symbol, or nearer none than `LEAST_CONFIDENCE` allows.
 */
export const recognisedSymbolOf = (shape: ShapeDescriptors): RecognisedSymbol | null => {
  let best: RecognisedSymbol | null = null;

  SYMBOL_TEMPLATES.forEach((template) => {
    if (!within(shape.holes, template.holes) || !within(shape.nested, template.nested)) return;
    if (shape.lengthMetres !== null && !within(shape.lengthMetres, template.metres)) return;

    const off = Object.entries(template.expect).reduce((total, [name, expected]) => {
      const value = shape[name as keyof typeof template.expect];
      return total + (Math.abs(value - expected.mean) / expected.spread);
    }, 0);

    const unsized = shape.lengthMetres === null ? UNSIZED_COST : 0;
    const confidence = Math.round((BEST_CONFIDENCE - (off * CONFIDENCE_PER_SPREAD) - unsized) * 100) / 100;

    if (confidence >= LEAST_CONFIDENCE && (!best || confidence > best.confidence)) {
      best = { kind: template.kind, confidence };
    }
  });

  return best;
};
